import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUpRight, Globe } from 'lucide-react'
import { EXPLORERS, type ExplorerEntry } from '@/config/explorers'
import SearchBar from '@/components/SearchBar'

const URL_LIKE_REGEX = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}([/?#]\S*)?$/i

const isLikelyUrl = (value: string) => URL_LIKE_REGEX.test(value.trim())

const toUrl = (value: string) => {
  const trimmed = value.trim()
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

const ExplorerIcon = ({ entry }: { entry: ExplorerEntry }) => {
  const [imgError, setImgError] = useState(false)

  return (
    <div className="w-8 h-8 rounded-full overflow-hidden bg-primary/20 flex items-center justify-center shrink-0">
      {entry.logoUrl && !imgError ? (
        <img
          src={entry.logoUrl}
          alt={entry.name}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <Globe className="w-4 h-4 text-primary" />
      )}
    </div>
  )
}

const Explorer = () => {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')

  const filteredExplorers = EXPLORERS.filter((entry) => {
    const term = searchTerm.toLowerCase()
    return (
      entry.name.toLowerCase().includes(term) ||
      entry.url.toLowerCase().includes(term)
    )
  })

  return (
    <div className="flex flex-col w-full px-2 gap-6 pt-4">
      <div className="flex items-center gap-4">
        <h1 className="text-4xl font-bold text-text-main">
          {t('sidebar.explore')}
        </h1>
      </div>

      <SearchBar
        placeholder={t('explorer.search_placeholder')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <div className="bg-card border border-border-base rounded-xl shadow-sm divide-y divide-border-base overflow-hidden">
        {filteredExplorers.length === 0 ? (
          isLikelyUrl(searchTerm) ? (
            <button
              onClick={() =>
                window.open(toUrl(searchTerm), '_blank', 'noopener,noreferrer')
              }
              className="w-full flex items-center justify-between gap-3 px-4 py-3.5 hover:bg-card-hover transition-colors text-left"
            >
              <span className="text-sm text-text-main truncate">
                {toUrl(searchTerm)}
              </span>
              <ArrowUpRight className="w-4 h-4 text-text-secondary shrink-0" />
            </button>
          ) : (
            <div className="p-6 text-center text-text-secondary text-sm">
              {t('explorer.no_results')}
            </div>
          )
        ) : (
          filteredExplorers.map((entry) => (
            <button
              key={entry.id}
              onClick={() =>
                window.open(entry.url, '_blank', 'noopener,noreferrer')
              }
              className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-card-hover transition-colors text-left"
            >
              <ExplorerIcon entry={entry} />
              <div className="flex flex-col min-w-0">
                <span className="text-md font-medium text-text-main">
                  {entry.name}
                </span>
                <span className="text-sm text-text-secondary line-clamp-2">
                  {t(entry.descriptionKey)}
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  )
}

export default Explorer
